// src/lib/scenarioTreeSummary.js
// Serializes the scenari_futuri branching tree (InfiniteCanvas.js's
// scenarioSplits/scenarioBranches state) into a compact indented-text
// summary the mentor prompt can read, so it knows the tree's actual shape
// instead of always defaulting to forking from "root" — the model needs a
// real node id to target, not just the "root" example in its own
// instructions. splits: [{id, afterNodeId, pending}]; branches:
// [{id, parentSplitId, label}]. Mirrored into the mentor prompt in all 3
// transports (backend's MentorGuideNode, mentorPrompt.js's
// buildMentorMessages) as plain interpolated text, not re-parsed — this is
// the single place the tree-walking logic lives.
export function buildScenarioTreeSummary(splits, branches) {
  if (!splits?.length && !branches?.length) return "";
  const lines = [];

  function describeNode(nodeId, label, depth, pending) {
    const suffix = pending ? " (in attesa)" : "";
    lines.push(`${"  ".repeat(depth)}- [${nodeId}] "${label}"${suffix}`);
    splits
      .filter((s) => s.afterNodeId === nodeId)
      .forEach((s) => {
        branches
          .filter((b) => b.parentSplitId === s.id)
          .forEach((b) => describeNode(b.id, b.label || "(senza nome)", depth + 1, s.pending));
      });
  }

  describeNode("root", "Oggi", 0, false);
  return lines.join("\n");
}

// Heuristic hint for which existing branch the user's latest message is
// most likely developing further — reduces reliance on the model's own
// reasoning for the common case, since a weak/local model can default to
// "root" even when explicitly told to prefer an existing branch (observed
// in practice, not just theoretical). Scores each branch by how many of
// its own label's significant words (4+ chars, so "di"/"per"/"con" don't
// count) appear in the latest message; returns the best-scoring branch's
// id, or null if nothing scores above zero (nothing to hint — the model
// reasons from full context on its own, per the prompt's own instruction).
export function guessActiveBranch(lastUserText, branches) {
  const text = (lastUserText || "").toLowerCase();
  if (!text || !branches?.length) return null;
  let best = null;
  let bestScore = 0;
  branches.forEach((b) => {
    const label = (b.label || "").toLowerCase();
    if (!label) return;
    const words = label.split(/\s+/).filter((w) => w.length >= 4);
    const score = words.filter((w) => text.includes(w)).length;
    if (score > bestScore) {
      bestScore = score;
      best = b.id;
    }
  });
  return best;
}

// Maps every branch (any depth) to its top-level ancestor's sequential
// number (root's direct children numbered 1, 2, 3… in creation order —
// which is also their visual top-to-bottom order: buildLayout's traversal
// is strictly left-to-right with no reordering, so the Nth level-1 branch
// processed always gets a leaf-slot range entirely after the (N-1)th's).
// Shared by ScenarioTimeline.jsx (shows the number only on the level-1
// pill itself) and ScenarioMatrix.jsx (shows it on every chip, since a
// deep chip still needs to read as "part of scenario 2" for the matrix to
// be useful) — single source of truth so the two views can't disagree.
export function computeScenarioNumbers(splits, branches) {
  const numberOf = {};
  splits
    .filter((s) => s.afterNodeId === "root")
    .forEach((s) => {
      branches
        .filter((b) => b.parentSplitId === s.id)
        .forEach((b) => { numberOf[b.id] = Object.keys(numberOf).length + 1; });
    });

  const branchById = new Map(branches.map((b) => [b.id, b]));
  const splitById = new Map(splits.map((s) => [s.id, s]));
  function ancestorNumber(branchId, seen) {
    if (numberOf[branchId] !== undefined) return numberOf[branchId];
    if (seen.has(branchId)) return null; // cycle guard against malformed data
    seen.add(branchId);
    const split = splitById.get(branchById.get(branchId)?.parentSplitId);
    if (!split || split.afterNodeId === "root") return null; // orphaned/malformed
    return ancestorNumber(split.afterNodeId, seen);
  }

  const result = {};
  branches.forEach((b) => { result[b.id] = ancestorNumber(b.id, new Set()); });
  return result;
}
